import React, {useEffect, useMemo} from "react";
import {useAuthState} from "react-firebase-hooks/auth";

import {
  GameServer,
  LivePlayerController,
  Player,
  RemotePlayerController,
  ReplayVerifier,
  World,
} from "@repo/shared";

import {submitReplayV2} from "~/api/hooks/useSubmitReplayV2";
import {useGlobalEventDispatch} from "~/contexts/event/GlobalEventBusContext";
import {useGame} from "~/contexts/game-manager/GameManagerProvider";
import {useGroupSession} from "~/contexts/session/GroupSessionProvider";
import {
  SetTabbingPayload,
  useUserInput,
} from "~/contexts/user-input/UserInputCoordinatorProvider";
import {auth} from "~/firebase/firebase";

import {usePlayerActionSync} from "./usePlayerActionSync";

/**
 * A self-contained component that sets up and manages a CO-OP game.
 * It uses specialized hooks for networking and host controls.
 */
const CoopGame: React.FC<{levelId: string; world: World}> = ({
  levelId,
  world,
}) => {
  const {gameManager} = useGame();
  const {emitter: userInputEmitter} = useUserInput();
  const {sessionData, isHost, startNewGameRound} = useGroupSession();
  const [user] = useAuthState(auth);
  const dispatch = useGlobalEventDispatch();

  const hasAlreadyJoinedThisRound = useMemo(() => {
    const currentRoundId = sessionData?.current_round?.roundId;
    if (!currentRoundId) return false;

    // Check session storage for the lock flag.
    const joinedRoundId = sessionStorage.getItem("joinedRoundId");
    return joinedRoundId === currentRoundId;
  }, [sessionData?.current_round?.roundId]);

  // Memoize game setup based on the stable round ID.
  // This ensures that players and controllers are only created ONCE per round.
  const {players, controllers, liveController, remoteControllers} =
    useMemo(() => {
      const round = sessionData?.current_round;
      const participants = round?.participants;

      if (!user || !participants) {
        return {
          players: [],
          controllers: [],
          liveController: null,
          remoteControllers: [],
        };
      }

      const newPlayers: Player[] = [];
      const newControllers: (LivePlayerController | RemotePlayerController)[] =
        [];

      for (const playerId in participants) {
        const player = new Player(playerId);
        newPlayers.push(player);
        // --- CRITICAL LOGIC CHANGE ---
        // If this is the local user, decide whether to give them a live controller
        // or lock them into a remote (spectator) controller.
        if (playerId === user.uid) {
          if (hasAlreadyJoinedThisRound) {
            // This player is reloading.
            // Lock them out.
            // They will spectate their own last-known actions.
            console.warn(
              `[CoopGame] Re-joining active round ${round.roundId}. Locking player to spectator mode.`,
            );
            newControllers.push(
              new RemotePlayerController(player.id, player.ownerId),
            );
          } else {
            // This is a fresh join. Give them a live controller.
            newControllers.push(new LivePlayerController(player.id));
          }
        } else {
          // For all other players, it's always a remote controller.
          newControllers.push(
            new RemotePlayerController(player.id, player.ownerId),
          );
        }
      }

      const liveCtrl = newControllers.find(
        (c) => c instanceof LivePlayerController,
      ) as LivePlayerController | null;
      const remoteCtrls = newControllers.filter(
        (c) => c instanceof RemotePlayerController,
      ) as RemotePlayerController[];

      return {
        players: newPlayers,
        controllers: newControllers,
        liveController: liveCtrl,
        remoteControllers: remoteCtrls,
      };
      // The dependency array now uses stable values.
      // The game entities will only be re-instantiated when the round or user actually changes.
    }, [user, sessionData?.current_round?.roundId, hasAlreadyJoinedThisRound]);

  usePlayerActionSync({
    participants: sessionData?.current_round?.participants || null,
    roundId: sessionData?.current_round?.roundId || null,
    localPlayerId: user?.uid || null,
    liveController: liveController,
    remoteControllers,
  });

  useEffect(() => {
    // Extract round data to create stable dependencies for the effect.
    const round = sessionData?.current_round;
    const roundId = round?.roundId;
    const startTime = round?.startTime;

    // Guard against running the effect with incomplete data.
    if (
      !gameManager ||
      !user ||
      !roundId ||
      !startTime ||
      players.length === 0
    ) {
      return;
    }

    sessionStorage.setItem("joinedRoundId", roundId);

    // This effect now correctly depends on the roundId.
    // It will only re-run when a new round starts.
    const coopServer = new GameServer("coop_main", world, players, controllers);
    const removeServer = gameManager.add(coopServer);
    gameManager.setPrimaryServer(coopServer.id);
    coopServer.start(startTime);

    const handleSetTabbing = (payload: SetTabbingPayload) => {
      if (payload.playerIndex === 0)
        liveController?.setTabbing(payload.isTabbing);
    };

    const handleReset = () => {
      if (isHost) startNewGameRound(levelId);
    };

    const handleRoundFinished = (_result: unknown, isPrimary: boolean) => {
      if (!isPrimary) return;

      const replay = coopServer.createReplay(levelId);
      submitReplayV2({
        levelId,
        userId: user.uid,
        replay,
      }).then((res) => {
        if (res.isNewPersonalBest)
          dispatch("new-personal-best", {replay: res.replay});
      });

      const verifier = new ReplayVerifier(world);
      const res = verifier.verify(replay);
      console.log("Replay Verification Result:", replay, res);
    };

    userInputEmitter.on("set-tabbing", handleSetTabbing);
    userInputEmitter.on("reset-simulation", handleReset);
    gameManager.on("round-finished", handleRoundFinished);

    return () => {
      console.log("Cleanup", coopServer);
      if (!coopServer.isRoundOver)
        submitReplayV2({
          levelId,
          userId: user.uid,
          replay: coopServer.createReplay(levelId),
        });
      userInputEmitter.off("set-tabbing", handleSetTabbing);
      userInputEmitter.off("reset-simulation", handleReset);
      gameManager.off("round-finished", handleRoundFinished);
      removeServer();
      gameManager.setPrimaryServer(null);
    };
    // The dependency array is now minimal and stable.
    // It correctly reflects the conditions under which the game server should be reset.
  }, [
    gameManager,
    user,
    world,
    levelId,
    isHost,
    players,
    controllers,
    liveController,
    userInputEmitter,
    startNewGameRound,
  ]);

  return null;
};

export default CoopGame;
